import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import { Grid, LinearProgress } from "@material-ui/core";
import api from "../../services/api";
import { has, get, isNull } from "lodash";
import CardCounter from "../../components/Dashboard/CardCounter";
import GroupIcon from "@material-ui/icons/Group";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import { useDate } from "../../hooks/useDate";
import { socketConnection } from "../../services/socket";

const STATUS_CONFIG = {
  INATIVA:      { label: "Inativa",       color: "#9e9e9e", bg: "#f5f5f5" },
  PROGRAMADA:   { label: "Programada",    color: "#1565c0", bg: "#e3f2fd" },
  EM_ANDAMENTO: { label: "Em Andamento",  color: "#2e7d32", bg: "#e8f5e9" },
  CANCELADA:    { label: "Cancelada",     color: "#c62828", bg: "#ffebee" },
  FINALIZADA:   { label: "Finalizada",    color: "#6a1b9a", bg: "#f3e5f5" },
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
}));

const CampaignReport = () => {
  const classes = useStyles();
  const { campaignId } = useParams();
  const history = useHistory();

  const [campaign, setCampaign] = useState({});
  const [validContacts, setValidContacts] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [confirmationRequested, setConfirmationRequested] = useState(0);
  const [confirmed, setConfirmed] = useState(0);
  const [percent, setPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  const { datetimeToClient } = useDate();

  useEffect(() => {
    if (mounted.current) findCampaign();
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mounted.current || !has(campaign, "shipping")) return;

    if (has(campaign, "contactList")) {
      const contactList = get(campaign, "contactList");
      const valids = contactList.contacts.filter((c) => c.isWhatsappValid);
      setValidContacts(valids.length);
    }

    const shipping = get(campaign, "shipping");
    const deliveredList = shipping.filter((c) => !isNull(c.deliveredAt));
    const requestedList = shipping.filter((c) => !isNull(c.confirmationRequestedAt));
    // "confirmado" = recebeu a mensagem de confirmação E respondeu (ambos preenchidos)
    const confirmedList = shipping.filter(
      (c) => !isNull(c.deliveredAt) && !isNull(c.confirmationRequestedAt)
    );
    setDelivered(deliveredList.length);
    setConfirmationRequested(requestedList.length);
    setConfirmed(confirmedList.length);
  }, [campaign]);

  useEffect(() => {
    if (validContacts > 0) {
      setPercent(Math.min((delivered / validContacts) * 100, 100));
    }
  }, [delivered, validContacts]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-campaign`, (data) => {
      if (data.record.id === +campaignId) {
        setCampaign(data.record);
        if (data.record.status === "FINALIZADA") {
          setTimeout(() => findCampaign(), 5000);
        }
      }
    });

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const findCampaign = async () => {
    setLoading(true);
    const { data } = await api.get(`/campaigns/${campaignId}`);
    setCampaign(data);
    setLoading(false);
  };

  const statusCfg = STATUS_CONFIG[campaign.status] || {};
  const percentFormatted = isNaN(percent) ? 0 : Math.round(percent);

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container alignItems="center" spacing={1}>
          <Grid item>
            <Tooltip title="Voltar para campanhas">
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => history.push("/campaigns")}
                variant="outlined"
                style={{ marginRight: 8 }}
              >
                Voltar
              </Button>
            </Tooltip>
          </Grid>
          <Grid item xs>
            <Title>Relatório — {campaign.name || "Campanha"}</Title>
          </Grid>
          <Grid item>
            <Tooltip title="Atualizar dados">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={findCampaign}
                disabled={loading}
                variant="outlined"
              >
                Atualizar
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Status + progresso */}
        <Box className={classes.headerRow}>
          {campaign.status && (
            <Chip
              label={statusCfg.label}
              size="small"
              style={{
                backgroundColor: statusCfg.bg,
                color: statusCfg.color,
                fontWeight: 700,
                fontSize: "0.8rem",
                height: 24,
              }}
            />
          )}
          <Typography variant="body2" color="textSecondary">
            {delivered} de {validContacts} contatos alcançados
          </Typography>
        </Box>

        {/* Barra de progresso com percentual */}
        <Box className={classes.progressLabel}>
          <Typography variant="caption" color="textSecondary">Progresso de entrega</Typography>
          <Typography variant="caption" style={{ fontWeight: 700 }}>
            {percentFormatted}%
          </Typography>
        </Box>
        <LinearProgress
          variant={loading ? "indeterminate" : "determinate"}
          style={{ height: 12, borderRadius: 6, marginBottom: 24 }}
          value={percentFormatted}
        />

        {/* Cards de métricas */}
        <Grid spacing={2} container>
          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<GroupIcon fontSize="inherit" />}
              title="Contatos Válidos"
              value={validContacts}
              loading={loading}
              color="#1565c0"
            />
          </Grid>

          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<CheckCircleIcon fontSize="inherit" />}
              title="Entregues"
              value={delivered}
              loading={loading}
              color="#2e7d32"
            />
          </Grid>

          {campaign.confirmation && (
            <>
              <Grid xs={12} md={4} item>
                <CardCounter
                  icon={<DoneIcon fontSize="inherit" />}
                  title="Confirmações Solicitadas"
                  value={confirmationRequested}
                  loading={loading}
                  color="#e65100"
                />
              </Grid>
              <Grid xs={12} md={4} item>
                <CardCounter
                  icon={<DoneAllIcon fontSize="inherit" />}
                  title="Confirmações Recebidas"
                  value={confirmed}
                  loading={loading}
                  color="#6a1b9a"
                />
              </Grid>
            </>
          )}

          {campaign.whatsappId && (
            <Grid xs={12} md={4} item>
              <CardCounter
                icon={<WhatsAppIcon fontSize="inherit" />}
                title="Conexão"
                value={campaign.whatsapp?.name}
                loading={loading}
                color="#25d366"
              />
            </Grid>
          )}

          {campaign.contactListId && (
            <Grid xs={12} md={4} item>
              <CardCounter
                icon={<ListAltIcon fontSize="inherit" />}
                title="Lista de Contatos"
                value={campaign.contactList?.name}
                loading={loading}
              />
            </Grid>
          )}

          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<ScheduleIcon fontSize="inherit" />}
              title="Agendamento"
              value={campaign.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "—"}
              loading={loading}
            />
          </Grid>

          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<EventAvailableIcon fontSize="inherit" />}
              title="Conclusão"
              value={campaign.completedAt ? datetimeToClient(campaign.completedAt) : "—"}
              loading={loading}
            />
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default CampaignReport;
